// ./src/components/generic/YearSelector/YearSelector.tsx
import React, { useMemo } from "react";
import { SelectProps } from "@mantine/core";
import dayjs from "dayjs";
import { SelectField } from "@/components/generic/form";

// ============================================
// Types
// ============================================

export interface YearSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Currently selected year */
  value: number | null;

  /** Callback when year changes */
  onChange: (year: number | null) => void;

  /** Minimum year to show (default: current year - 10) */
  minYear?: number;

  /** Maximum year to show (default: current year) */
  maxYear?: number;

  required?: boolean;

  /** Placeholder text */
  placeholder?: string;
}

// ============================================
// Constants
// ============================================

const DEFAULT_YEARS_BACK = 10;

// ============================================
// Component
// ============================================

export const YearSelector: React.FC<YearSelectorProps> = ({
  value,
  onChange,
  minYear,
  maxYear,
  clearable,
  placeholder = "Select year",
  size = "md",
  required = true,
  ...rest
}) => {
  // Calculate year range
  const currentYear = useMemo(() => dayjs().year(), []);

  const yearRange = useMemo(() => {
    const min = minYear ?? currentYear - DEFAULT_YEARS_BACK;
    const max = maxYear ?? currentYear;
    return { min, max };
  }, [minYear, maxYear, currentYear]);

  // Generate year options (descending order - most recent first)
  const yearOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];

    for (let year = yearRange.max; year >= yearRange.min; year--) {
      options.push({
        value: year.toString(),
        label: year.toString(),
      });
    }

    return options;
  }, [yearRange]);

  // Handle change - convert string to number
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue === null) {
      onChange(null);
    } else {
      const yearNumber = parseInt(selectedValue, 10);
      onChange(isNaN(yearNumber) ? null : yearNumber);
    }
  };

  return (
    <SelectField
      data={yearOptions}
      value={value?.toString() ?? null}
      onChange={(_name, v) => handleChange(v)}
      // A caller's `clearable` wins; otherwise an optional year can be cleared (#129: the prop was read and dropped).
      clearable={clearable ?? !required}
      placeholder={placeholder}
      size={size}
      {...rest}
    />
  );
};

export default YearSelector;
